<template>
  <div :class="mobileView ? 'h-full' : 'mt-2'">
    <div :class="mobileView ? 'bg-white rounded-lg shadow-md h-full flex flex-col' : 'bg-white rounded-lg shadow-md'">
      <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center" :class="mobileView ? 'flex-shrink-0' : ''">
        <h3 class="text-lg font-semibold text-gray-900">{{ t('modals.survey_batches.survey_clusters') }}</h3>
        <button
          v-if="!viewOnly"
          @click="navigateToCreateBatch"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
        >
          {{ t('modals.survey_batches.create_cluster') }}
        </button>
      </div>
      
      <div v-if="isLoading" :class="mobileView ? 'px-6 py-8 text-center flex-1 flex items-center justify-center' : 'px-6 py-8 text-center'">
        <div class="text-gray-500">{{ t('modals.survey_batches.loading_batches') }}</div>
      </div>
      
      <div v-else-if="batches.length === 0" :class="mobileView ? 'px-6 py-8 text-center flex-1 flex items-center justify-center' : 'px-6 py-8 text-center'">
        <div class="text-gray-500">{{ t('modals.survey_batches.no_batches_found') }}</div>
      </div>
      
      <!-- Scrollable Stacked List View -->
      <div :class="mobileView ? 'flex-1 overflow-y-auto' : 'max-h-[400px] md:max-h-[500px] overflow-y-auto'">
        <div class="divide-y divide-gray-200">
          <div 
            v-for="batch in batches" 
            :key="batch.id"
            class="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
            @click="navigateToBatchDetail(batch)"
          >
            <div class="flex flex-col space-y-3">
              <!-- Header with Batch Name and State -->
              <div class="flex items-start justify-between">
                <h4 class="text-base font-medium text-gray-900 truncate flex-1">
                  {{ batch.batch_name || 'Unnamed Batch' }}
                </h4>
                <span 
                  class="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0"
                  :class="getStateClass(batch.state)"
                >
                  {{ formatState(batch.state) }}
                </span>
              </div>

              <!-- Details Grid -->
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div class="flex flex-col">
                  <span class="text-xs font-medium text-gray-500">{{ t('modals.survey_batches.creation_date') }}</span>
                  <span class="text-gray-900">{{ formatDate(batch.created_at) }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-medium text-gray-500">{{ t('modals.survey_batches.due_date') }}</span>
                  <span class="text-gray-900">{{ batch.due_date ? formatDate(batch.due_date) : 'Not set' }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-medium text-gray-500">{{ t('modals.survey_batches.surveyor') }}</span>
                  <span class="text-gray-900 truncate">{{ batch.surveyor_name }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-medium text-gray-500">{{ t('modals.survey_batches.num_villages') }}</span>
                  <span class="text-gray-900">{{ batch.num_villages }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineEmits, onUnmounted } from 'vue'
import FlashMessageService from '@services/FlashMessages'
import GlobalEventBus from '@services/GlobalEventBus'
import i18n from '@utils/i18n'

const { t } = i18n.global

const emit = defineEmits(['create-cluster'])

const props = defineProps({
  targetAreaExternalId: {
    type: String,
    required: true
  },
  trainingCenterName: {
    type: String,
    required: true
  },
  trainingCenterId: {
    type: [String, Number],
    required: true
  },
  preselectedVillages: {
    type: Array,
    default: () => []
  },
  viewOnly: {
    type: Boolean,
    default: false
  },
  mobileView: {
    type: Boolean,
    default: false
  }
})

const batches = ref([])
const isLoading = ref(false)

let unsubscribeBatchCreated = null

onMounted(async () => {
  await fetchBatches()
  
  // Subscribe to batch-created events to refresh the list
  unsubscribeBatchCreated = GlobalEventBus.subscribe('batch-created', (batchData) => {
    // Only refresh if the batch was created for the current target area
    if (batchData.targetAreaId === props.targetAreaExternalId) {
      fetchBatches()
    }
  })
})

onUnmounted(() => {
  // Clean up event subscription
  if (unsubscribeBatchCreated) {
    unsubscribeBatchCreated()
  }
})

async function fetchBatches() {
  try {
    if (props.targetAreaExternalId) {
      isLoading.value = true
      const response = await fetch(`${location.origin}/target_areas/${props.targetAreaExternalId}/batches`)
      
      if (response.status === 404) {
        // Target area not yet synced or has no batches — treat as empty
        batches.value = []
        isLoading.value = false
        return
      }

      if (!response.ok) {
        throw new Error('Failed to fetch batches')
      }
      
      const data = await response.json()
      console.log("Received batches from server:", data)
      batches.value = data
      isLoading.value = false
    }
  } catch (error) {
    console.error('Error fetching batches:', error)
    FlashMessageService.error('Failed to fetch batches')
    isLoading.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return 'Not set'

  let date = null

  // THIS WILL ONLY WORK IF STRING IS FORMATTED LIKE THIS: "YYYY-DD-MM"
  // If it matches, treat as local date, otherwise treat as datetime (we need this since due_date and created_at are formatted differently)
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Date-only format - create as local date to avoid timezone conversion
    const [year, month, day] = dateStr.split('-').map(Number)
    date = new Date(year, month - 1, day)
  } else {
    date = new Date(dateStr)
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatState(state) {
  if (!state) return 'Unknown'
  const stateMap = {
    'draft': t('modals.survey_batches.draft_label'),
    'assigned': t('modals.survey_batches.assigned_label'),
    'ready_for_survey': t('modals.survey_batches.ready_for_survey_label'),
    'in_progress': t('modals.survey_batches.in_progress_label'),
    'completed': t('modals.survey_batches.completed_label'),
    'cancelled': t('modals.survey_batches.cancelled_label')
  }
  return stateMap[state] || state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, ' ')
}

function getStateClass(state) {
  if (!state) return 'bg-gray-100 text-gray-800'
  const stateMap = {
    'assigned': 'bg-yellow-100 text-yellow-800',
    'ready_for_survey': 'bg-blue-100 text-blue-800',
  }
  return stateMap[state] || 'bg-gray-100 text-gray-800'
}

function navigateToBatchDetail(batch) {
  window.location.href = `/batches/${batch.id}`
}

function navigateToCreateBatch() {
  if (props.preselectedVillages.length > 0) {
    const data = {
      villages: props.preselectedVillages,
      timestamp: Date.now(),
      targetAreaId: props.targetAreaExternalId
    }
    localStorage.setItem('batch_create_preselected_villages', JSON.stringify(data))
  }
  window.location.href = `/batches/new?target_area_id=${props.targetAreaExternalId}`
}
</script> 